import { Link } from "react-router-dom";
import { Mail, Phone, BadgeCheck } from "lucide-react";
import Logo from "./Logo";
import { FooterReviewLink } from "./GoogleReviewLink";
import { BRAND, CONTACT, SERVICES, CONDITIONS } from "../data/site";
import { COUNTIES } from "../data/counties";

export default function Footer() {
  return (
    <footer className="bg-white border-t border-[var(--line)]" data-testid="site-footer">
      <div className="container-site py-14 grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <Logo />
          <p className="mt-5 text-sm text-body leading-relaxed max-w-xs">
            Secure, in-network online therapy for kids, teens, adults, couples, and families across all 21 NJ counties.
          </p>
          <p className="mt-4 text-sm font-medium text-[var(--ink)]" data-testid="footer-affiliation">
            Affiliated with{" "}
            <a href={BRAND.affiliateUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 decoration-[var(--sage)] hover:text-[var(--sage)]" data-testid="footer-affiliation-link">
              {BRAND.affiliate}
            </a>
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            <li>
              <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 text-[var(--ink)] hover:text-[var(--sage)]" data-testid="footer-phone">
                <Phone className="h-4 w-4 text-[var(--sage)]" /> {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={CONTACT.emailHref} className="inline-flex items-center gap-2 text-[var(--ink)] hover:text-[var(--sage)] break-all" data-testid="footer-email">
                <Mail className="h-4 w-4 text-[var(--sage)] shrink-0" /> {CONTACT.email}
              </a>
            </li>
          </ul>
          <a
            href={CONTACT.psychologyToday}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-3.5 py-2 text-xs text-[var(--ink)] hover:border-[var(--sage)]"
            data-testid="footer-psychology-today-link"
          >
            <BadgeCheck className="h-4 w-4 text-[var(--sage)]" /> Verified by Psychology Today
          </a>
          <div className="mt-3">
            <FooterReviewLink />
          </div>
        </div>

        <FooterCol title="Services" className="md:col-span-2">
          {SERVICES.map((s) => (
            <FooterLink key={s.slug} to={`/services/${s.slug}`}>{s.title}</FooterLink>
          ))}
        </FooterCol>

        <FooterCol title="Specialties" className="md:col-span-2">
          {CONDITIONS.map((c) => (
            <FooterLink key={c.slug} to={`/conditions/${c.slug}`}>{c.title} Therapy</FooterLink>
          ))}
          <div className="pt-3" />
          <FooterLink to="/about">About Us</FooterLink>
          <FooterLink to="/team">Meet the Team</FooterLink>
          <FooterLink to="/faq">FAQ</FooterLink>
          <FooterLink to="/resources">Resources</FooterLink>
          <FooterLink to="/contact">Contact</FooterLink>
        </FooterCol>

        <FooterCol title="Areas We Serve" className="md:col-span-4">
          <div className="grid grid-cols-2 gap-x-4">
            {COUNTIES.map((c) => (
              <FooterLink key={c.slug} to={`/areas-we-serve/${c.slug}`}>{c.name} County</FooterLink>
            ))}
          </div>
        </FooterCol>
      </div>

      <div className="border-t border-[var(--line)]">
        <div className="container-site py-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between text-xs text-body">
          <p>
            © {new Date().getFullYear()} {BRAND.name}. Affiliated with {BRAND.affiliate}. All rights reserved.
          </p>
          <p data-testid="footer-crisis">
            In crisis? Call or text <a href="tel:988" className="font-semibold text-[var(--ink)]">988</a> or call 911. We are not a crisis service.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children, className = "" }) {
  return (
    <div className={className}>
      <h2 className="font-ui text-xs font-semibold uppercase tracking-[0.14em] text-[var(--ink)] mb-4">{title}</h2>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <Link to={to} className="py-1 text-sm text-body hover:text-[var(--sage)] transition-colors">
      {children}
    </Link>
  );
}
